import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Undo2, Redo2, Trash2, Magnet, Grid3x3,
  CheckCircle2, MapPin, ArrowLeft, Pencil, Move,
  Lock, Unlock, Copy, RotateCw, FileText, AlertTriangle,
  Sparkles, X, Printer,
} from "lucide-react";

const UNITS = [
  { v: "ft", l: "Feet" }, { v: "in", l: "Inches" },
  { v: "mm", l: "Millimetres" }, { v: "m", l: "Metres" },
];

const MARKER_KINDS = [
  { v: "entrance", l: "Entrance", c: "#16a34a" },
  { v: "shutter", l: "Shutter", c: "#0ea5e9" },
  { v: "billing_counter", l: "Billing counter", c: "#9333ea" },
  { v: "window", l: "Window", c: "#0891b2" },
  { v: "column", l: "Column", c: "#dc2626" },
  { v: "unusable", l: "Unusable", c: "#a16207" },
  { v: "staircase", l: "Staircase", c: "#475569" },
  { v: "washroom", l: "Washroom", c: "#0f766e" },
  { v: "storage", l: "Storage", c: "#3f6212" },
  { v: "no_rack", l: "No-rack zone", c: "#b91c1c" },
  { v: "preferred_display_wall", l: "Preferred display wall", c: "#FF6B1A" },
];
const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ptLabel = (i) => (i < 26 ? ALPHA[i] : ALPHA[Math.floor(i / 26) - 1] + ALPHA[i % 26]);
const fmt = (v, d = 2) => Number(v || 0).toFixed(d);

// ----- canvas constants -----
const VIEW = 1000;
const PAD = 60;
const CELL_PX = 20;            // 1 cell on canvas = 20px
const MIN_AISLE_IN = 36;       // walkway warning threshold in inches

// rotate (cx,cy) around (ox,oy) by deg
const rotPt = (x, y, ox, oy, deg) => {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return { x: ox + (x - ox) * c - (y - oy) * s, y: oy + (x - ox) * s + (y - oy) * c };
};

// Axis-aligned BB of a rotated rectangle (used for fast collision)
const itemBox = (it) => {
  const cx = it.x + it.lengthPx / 2, cy = it.y + it.depthPx / 2;
  const corners = [
    rotPt(it.x, it.y, cx, cy, it.rotation),
    rotPt(it.x + it.lengthPx, it.y, cx, cy, it.rotation),
    rotPt(it.x + it.lengthPx, it.y + it.depthPx, cx, cy, it.rotation),
    rotPt(it.x, it.y + it.depthPx, cx, cy, it.rotation),
  ];
  const xs = corners.map((p) => p.x), ys = corners.map((p) => p.y);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys), corners };
};

const boxesOverlap = (a, b, slack = 1.5) =>
  a.x1 + slack < b.x2 && a.x2 > b.x1 + slack && a.y1 + slack < b.y2 && a.y2 > b.y1 + slack;

// point-in-polygon
const inside = (px, py, poly) => {
  let res = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-9) + xi;
    if (intersect) res = !res;
  }
  return res;
};

export default function FloorPlanEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  // meta
  const [name, setName] = useState("Untitled Layout");
  const [unit, setUnit] = useState("in");
  const [gridSize, setGridSize] = useState(12);   // inches per cell (default 1 ft/cell)
  const [client, setClient] = useState({ name: "", company: "", contact: "", address: "", gstin: "" });
  const [notes, setNotes] = useState("");
  const [planNumber, setPlanNumber] = useState("");
  const [layoutId, setLayoutId] = useState(id || null);

  // shape
  const [points, setPoints] = useState([]);
  const [closed, setClosed] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [markers, setMarkers] = useState([]);

  // placed items (manual layout)
  const [placed, setPlaced] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingLib, setPendingLib] = useState(null); // library item to drop next

  // canvas modes
  const [mode, setMode] = useState("draw"); // 'draw' | 'manual'
  const [snap, setSnap] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [drag, setDrag] = useState(null);
  const [markerDrag, setMarkerDrag] = useState(null);
  const [pendingMarkerKind, setPendingMarkerKind] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [itemDrag, setItemDrag] = useState(null); // {id, dx, dy}

  // library
  const [library, setLibrary] = useState([]);
  useEffect(() => { api.get("/floorplan/library").then((r) => setLibrary(r.data)); }, []);

  // auto-layout config + modal
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoConfig, setAutoConfig] = useState({
    preferred_bay_in: 36,
    wall_depth_in: 18,
    centre_depth_in: 36,
    centre_bay_in: 36,
    aisle_pref_in: 48,
    aisle_min_in: 36,
    include_corners: true,
    corner_size_in: 24,
    include_centre: true,
    max_centre_rows: 4,
    auto_billing: true,
    billing_length_in: 60,
    billing_depth_in: 24,
    entrance_clear_in: 96,
    replace_existing: true,
  });
  const [autoPrompted, setAutoPrompted] = useState(false);

  // history
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const snapshot = () => ({
    points: structuredClone(points), closed, overrides: { ...overrides },
    markers: structuredClone(markers), placed: structuredClone(placed),
  });
  const pushHistory = () => { setHistory((h) => [...h.slice(-50), snapshot()]); setFuture([]); };
  const undo = () => {
    if (!history.length) return;
    const p = history[history.length - 1];
    setFuture((f) => [snapshot(), ...f]);
    setHistory((h) => h.slice(0, -1));
    setPoints(p.points); setClosed(p.closed); setOverrides(p.overrides);
    setMarkers(p.markers); setPlaced(p.placed || []);
  };
  const redo = () => {
    if (!future.length) return;
    const n = future[0];
    setHistory((h) => [...h, snapshot()]);
    setFuture((f) => f.slice(1));
    setPoints(n.points); setClosed(n.closed); setOverrides(n.overrides);
    setMarkers(n.markers); setPlaced(n.placed || []);
  };

  // load
  useEffect(() => {
    if (isNew) return;
    api.get(`/floorplan/layouts/${id}`).then(({ data }) => {
      setName(data.name); setUnit(data.unit); setGridSize(data.grid_size);
      setClient(data.client || {}); setNotes(data.notes || ""); setPlanNumber(data.plan_number);
      setPoints(data.points); setClosed(true); setLayoutId(data.id);
      const oMap = {};
      (data.side_overrides || []).forEach((o) => { oMap[o.index] = o.length; });
      setOverrides(oMap);
      setMarkers(data.markers || []);
      setPlaced((data.placed_items || []).map((p) => ({ ...p })));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id, isNew]);

  // ---- conversions ----
  const px2unit = (px) => (px / CELL_PX) * gridSize;
  const unit2px = (u) => (u / gridSize) * CELL_PX;

  const svgRef = useRef(null);
  const screenToCanvas = (e, doSnap = true) => {
    const svg = svgRef.current; if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM().inverse(); const local = pt.matrixTransform(ctm);
    let x = local.x, y = local.y;
    if (snap && doSnap) { x = Math.round(x / CELL_PX) * CELL_PX; y = Math.round(y / CELL_PX) * CELL_PX; }
    return { x, y };
  };

  // ---- DRAW mode handlers ----
  const onCanvasClickDraw = (e) => {
    if (closed) return;
    const p = screenToCanvas(e);
    if (points.length >= 3) {
      const f = points[0]; const dx = p.x - f.x, dy = p.y - f.y;
      if (Math.sqrt(dx * dx + dy * dy) < CELL_PX) { pushHistory(); setClosed(true); return; }
    }
    pushHistory(); setPoints((pts) => [...pts, p]);
  };

  // ---- MANUAL mode handlers ----
  // Convert placed item's physical dimensions to canvas px.
  // Prefer inch-based fields (new rows) and fall back to legacy feet fields.
  const placedItemsForCanvas = useMemo(() => placed.map((p) => {
    const isInch = (unit === "in") || (p.length_in != null);
    const lenPhys = p.length_in != null ? p.length_in : p.length_ft;
    const depPhys = p.depth_in  != null ? p.depth_in  : p.depth_ft;
    const lengthPx = (lenPhys / gridSize) * CELL_PX;
    const depthPx  = (depPhys  / gridSize) * CELL_PX;
    return { ...p, lengthPx, depthPx, isInch };
  }), [placed, gridSize, unit]);

  const dropLibraryItem = (lib, atX, atY) => {
    pushHistory();
    const lIn = lib.default_length_in ?? (lib.default_length_ft * 12);
    const dIn = lib.default_depth_in  ?? (lib.default_depth_ft  * 12);
    const hIn = lib.default_height_in ?? (lib.default_height_ft * 12);
    const lPx = (lIn / gridSize) * CELL_PX;
    const dPx = (dIn / gridSize) * CELL_PX;
    const newItem = {
      id: crypto.randomUUID(),
      library_item_id: lib.id,
      name: lib.name,
      kind: lib.kind,
      x: atX - lPx / 2,
      y: atY - dPx / 2,
      length_ft: lIn / 12,
      depth_ft: dIn / 12,
      height_ft: hIn / 12,
      length_in: lIn,
      depth_in: dIn,
      height_in: hIn,
      bays: [lIn],
      unit_types: [lib.unit_type || "main"],
      rotation: 0,
      locked: false,
    };
    setPlaced((arr) => [...arr, newItem]);
    setSelectedId(newItem.id);
    setPendingLib(null);
  };

  const onCanvasClickManual = (e) => {
    // Marker placement
    if (pendingMarkerKind) {
      const p = screenToCanvas(e);
      pushHistory();
      setMarkers((m) => [...m, { id: crypto.randomUUID(), kind: pendingMarkerKind, x: p.x, y: p.y, width: 0, height: 0, rotation: 0, label: "" }]);
      setPendingMarkerKind(null);
      return;
    }
    // Library drop
    if (pendingLib) {
      const p = screenToCanvas(e);
      dropLibraryItem(pendingLib, p.x, p.y);
      return;
    }
    // Click background = deselect
    setSelectedId(null);
  };

  // ---- Common SVG mouse handlers ----
  const onPointDown = (i, e) => { e.stopPropagation(); if (mode === "draw") setDrag(i); };
  const onItemDown = (it, e) => {
    e.stopPropagation();
    if (it.locked) return;
    setSelectedId(it.id);
    const p = screenToCanvas(e, false);
    setItemDrag({ id: it.id, dx: p.x - it.x, dy: p.y - it.y });
  };
  const onMouseMove = (e) => {
    const p = screenToCanvas(e, mode === "draw");
    setHoverPos(p);
    if (drag !== null) {
      setPoints((pts) => pts.map((q, j) => (j === drag ? p : q)));
    }
    if (markerDrag !== null) {
      const ps = screenToCanvas(e);
      setMarkers((arr) => arr.map((m, j) => (j === markerDrag ? { ...m, x: ps.x, y: ps.y } : m)));
    }
    if (itemDrag) {
      const raw = screenToCanvas(e, false);
      let nx = raw.x - itemDrag.dx;
      let ny = raw.y - itemDrag.dy;
      if (snap) {
        nx = Math.round(nx / CELL_PX) * CELL_PX;
        ny = Math.round(ny / CELL_PX) * CELL_PX;
      }
      setPlaced((arr) => arr.map((it) => (it.id === itemDrag.id ? { ...it, x: nx, y: ny } : it)));
    }
  };
  const onMouseUp = () => {
    if (drag !== null) pushHistory();
    if (itemDrag) pushHistory();
    setDrag(null); setMarkerDrag(null); setItemDrag(null);
  };

  const deletePoint = (i) => {
    pushHistory();
    setPoints((pts) => pts.filter((_, j) => j !== i));
    if (points.length - 1 < 3) setClosed(false);
    setOverrides({});
  };

  // ---- sides ----
  const sides = useMemo(() => {
    const out = []; if (points.length < 2) return out;
    const n = closed ? points.length : points.length - 1;
    for (let i = 0; i < n; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      const dx = b.x - a.x, dy = b.y - a.y;
      const lenPx = Math.sqrt(dx * dx + dy * dy);
      const computedLen = px2unit(lenPx);
      const len = overrides[i] != null ? overrides[i] : computedLen;
      out.push({
        index: i, a, b, lenPx, computedLen, len,
        override: overrides[i] != null,
        midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2, dx, dy,
        label: `${ptLabel(i)}–${ptLabel((i + 1) % points.length)}`,
      });
    }
    return out;
  }, [points, closed, overrides, gridSize]);

  const stats = useMemo(() => {
    if (!closed || points.length < 3) return { area: 0, perimeter: 0 };
    let s = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      s += a.x * b.y - b.x * a.y;
    }
    const area = (Math.abs(s) / 2) * (gridSize / CELL_PX) ** 2;
    const perimeter = sides.reduce((sum, sd) => sum + sd.len, 0);
    return { area, perimeter };
  }, [points, closed, sides, gridSize]);

  // ---- Live warnings ----
  const warnings = useMemo(() => {
    if (mode !== "manual" || !closed || placedItemsForCanvas.length === 0) return [];
    const out = [];
    const boxes = placedItemsForCanvas.map((it) => ({ ...itemBox(it), it }));

    // Overlap (item-vs-item) — skip pairs of corner racks (L-arms share the corner column)
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i].it, b = boxes[j].it;
        if (a.kind === "corner_rack" && b.kind === "corner_rack") continue;
        if (boxesOverlap(boxes[i], boxes[j])) {
          out.push({ kind: "overlap", level: "error", message: `${a.name} overlaps ${b.name}`, ids: [a.id, b.id] });
        }
      }
    }

    // Walkway clearance: any pair of items closer than MIN_AISLE_IN
    const minAislePx = unit2px(MIN_AISLE_IN);
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i], b = boxes[j];
        if (boxesOverlap(a, b)) continue;
        const dx = Math.max(0, Math.max(a.x1 - b.x2, b.x1 - a.x2));
        const dy = Math.max(0, Math.max(a.y1 - b.y2, b.y1 - a.y2));
        const gap = Math.sqrt(dx * dx + dy * dy);
        if (gap > 0 && gap < minAislePx) {
          out.push({ kind: "walkway", level: "warn", message: `Walkway < ${MIN_AISLE_IN}″ between ${a.it.name} and ${b.it.name}`, ids: [a.it.id, b.it.id] });
          break; // dedupe per item
        }
      }
    }

    // Blocked entrance: any item within 3 ft of an entrance/shutter marker
    const blockRadiusPx = unit2px(MIN_AISLE_IN);
    const entrances = markers.filter((m) => m.kind === "entrance" || m.kind === "shutter");
    boxes.forEach((b) => {
      entrances.forEach((m) => {
        if (m.x >= b.x1 - blockRadiusPx && m.x <= b.x2 + blockRadiusPx &&
            m.y >= b.y1 - blockRadiusPx && m.y <= b.y2 + blockRadiusPx) {
          out.push({ kind: "blocked_entrance", level: "error", message: `${b.it.name} blocks the ${m.kind === "entrance" ? "entrance" : "shutter"}`, ids: [b.it.id] });
        }
      });
    });

    // Outside polygon (item not fully inside)
    boxes.forEach((b) => {
      const allInside = b.corners.every((c) => inside(c.x, c.y, points));
      if (!allInside) out.push({ kind: "outside", level: "error", message: `${b.it.name} is outside the floor boundary`, ids: [b.it.id] });
    });

    // Alignment: any 3+ items along a wall but mis-aligned y or x
    // simple heuristic: group items by closest wall, warn if their offset to the wall varies > 1 ft
    if (sides.length > 0) {
      sides.forEach((sd) => {
        // wall direction
        const dx = sd.dx, dy = sd.dy;
        const wlen = Math.sqrt(dx * dx + dy * dy) || 1;
        const ux = dx / wlen, uy = dy / wlen; // along wall
        const nxv = -uy, nyv = ux;             // normal to wall
        const offsets = [];
        boxes.forEach((b) => {
          const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
          const off = (cx - sd.a.x) * nxv + (cy - sd.a.y) * nyv;
          if (Math.abs(off) < unit2px(48)) offsets.push({ b, off });   // "near wall" = within 48″
        });
        if (offsets.length >= 3) {
          const minO = Math.min(...offsets.map((o) => o.off));
          const maxO = Math.max(...offsets.map((o) => o.off));
          if (maxO - minO > unit2px(12)) {   // alignment tolerance 12″
            out.push({ kind: "alignment", level: "warn", message: `Items along wall ${sd.label} not in a straight row`, ids: offsets.map((o) => o.b.it.id) });
          }
        }
      });
    }

    // Underused wall space: a wall longer than 8 ft with no item within 2 ft of it
    sides.forEach((sd) => {
      if (sd.len < 96) return;   // only warn on walls ≥ 96″ (8 ft) without any racks
      const dx = sd.dx, dy = sd.dy;
      const wlen = Math.sqrt(dx * dx + dy * dy) || 1;
      const nxv = -dy / wlen, nyv = dx / wlen;
      const near = boxes.some((b) => {
        const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
        const off = Math.abs((cx - sd.a.x) * nxv + (cy - sd.a.y) * nyv);
        return off < unit2px(24);   // rack considered "near wall" if within 24″
      });
      if (!near) out.push({ kind: "underused_wall", level: "info", message: `Wall ${sd.label} (${fmt(sd.len)} ${unit}) has no racks`, ids: [] });
    });

    // Dead corner: angle > 200° (reflex vertex) without any rack near
    for (let i = 0; i < points.length; i++) {
      const prev = points[(i - 1 + points.length) % points.length];
      const cur = points[i];
      const next = points[(i + 1) % points.length];
      const a1 = Math.atan2(prev.y - cur.y, prev.x - cur.x);
      const a2 = Math.atan2(next.y - cur.y, next.x - cur.x);
      let theta = a2 - a1; if (theta < 0) theta += Math.PI * 2;
      const deg = (theta * 180) / Math.PI;
      if (deg > 200) {
        const near = boxes.some((b) => {
          const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
          return Math.hypot(cx - cur.x, cy - cur.y) < unit2px(48);
        });
        if (!near) out.push({ kind: "dead_corner", level: "info", message: `Dead corner at ${ptLabel(i)} not utilised`, ids: [] });
      }
    }

    return out;
  }, [mode, closed, placedItemsForCanvas, points, sides, markers, gridSize, unit]);

  // ----- actions -----
  const setSideLen = (i, v) => {
    pushHistory();
    if (v === "" || isNaN(v)) { const n = { ...overrides }; delete n[i]; setOverrides(n); }
    else setOverrides({ ...overrides, [i]: Number(v) });
  };
  const reset = () => { pushHistory(); setPoints([]); setClosed(false); setOverrides({}); setMarkers([]); setPlaced([]); };
  const removeMarker = (mid) => { pushHistory(); setMarkers((m) => m.filter((x) => x.id !== mid)); };
  const removeItem = (iid) => { pushHistory(); setPlaced((arr) => arr.filter((x) => x.id !== iid)); if (selectedId === iid) setSelectedId(null); };
  const duplicateItem = (iid) => {
    pushHistory();
    setPlaced((arr) => {
      const it = arr.find((x) => x.id === iid); if (!it) return arr;
      return [...arr, { ...it, id: crypto.randomUUID(), x: it.x + 30, y: it.y + 30, locked: false }];
    });
  };
  const rotateItem = (iid, deg = 90) => {
    pushHistory();
    setPlaced((arr) => arr.map((x) => (x.id === iid ? { ...x, rotation: ((x.rotation + deg) % 360 + 360) % 360 } : x)));
  };
  const toggleLock = (iid) => {
    setPlaced((arr) => arr.map((x) => (x.id === iid ? { ...x, locked: !x.locked } : x)));
  };
  const resizeItem = (iid, lengthFt, depthFt) => {
    pushHistory();
    setPlaced((arr) => arr.map((x) => {
      if (x.id !== iid) return x;
      const lib = library.find((l) => l.id === x.library_item_id);
      const minL = lib?.min_length_ft ?? 1, maxL = lib?.max_length_ft ?? 24;
      const minD = lib?.min_depth_ft ?? 0.5, maxD = lib?.max_depth_ft ?? 6;
      return {
        ...x,
        length_ft: Math.max(minL, Math.min(maxL, lengthFt)),
        depth_ft: Math.max(minD, Math.min(maxD, depthFt)),
      };
    }));
  };

  // ---- save layout ----
  const save = async () => {
    if (!closed || points.length < 3) { toast.error("Close the shape first"); return; }
    const body = {
      name, client, unit, grid_size: gridSize,
      points: points.map((p) => ({ x: p.x, y: p.y })),
      side_overrides: Object.entries(overrides).map(([i, l]) => ({ index: Number(i), length: Number(l) })),
      markers: markers.map((m) => ({ ...m })),
      placed_items: placed.map((p) => ({ ...p })),
      notes,
    };
    try {
      if (isNew && !layoutId) {
        const { data } = await api.post("/floorplan/layouts", body);
        setLayoutId(data.id); setPlanNumber(data.plan_number);
        toast.success(`Layout ${data.plan_number} saved`);
        navigate(`/floorplan/${data.id}`, { replace: true });
      } else {
        const lid = layoutId || id;
        await api.put(`/floorplan/layouts/${lid}`, body);
        toast.success("Layout updated");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // ---- generate quotation ----
  const generateQuote = async () => {
    if (placed.length === 0) { toast.error("Add at least one rack first"); return; }
    if (!layoutId) { toast.error("Save the layout first"); return; }
    try {
      const { data } = await api.post("/floorplan/quotations", { layout_id: layoutId, notes });
      toast.success(`Quote ${data.quote_number} generated`);
      navigate(`/floorplan-quotations/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // ---- auto-layout ----
  const runAutoLayout = async () => {
    if (!closed || points.length < 3) { toast.error("Close the floor shape first"); return; }
    setAutoBusy(true);
    try {
      const { data } = await api.post("/floorplan/auto-layout", {
        points: points.map((p) => ({ x: p.x, y: p.y })),
        markers: markers.map((m) => ({ kind: m.kind, x: m.x, y: m.y })),
        config: { ...autoConfig, grid_size: gridSize },
      });
      pushHistory();
      const newItems = data.placed_items || [];
      if (autoConfig.replace_existing) {
        setPlaced(newItems);
      } else {
        setPlaced((arr) => [...arr, ...newItems]);
      }
      setMode("manual");
      setAutoOpen(false);
      const s = data.summary || {};
      toast.success(
        `Auto-layout: ${s.total_rows || 0} rows · ${s.total_bays || 0} bays (${s.wall_bays || 0} wall · ${s.corners || 0} corner · ${s.centre_bays || 0} centre) · ${s.total_legs || 0} legs`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAutoBusy(false);
    }
  };

  // Auto-prompt once when user closes the polygon in draw mode
  useEffect(() => {
    if (closed && !autoPrompted && mode === "draw" && points.length >= 3 && placed.length === 0) {
      setAutoPrompted(true);
      setAutoOpen(true);
    }
  }, [closed, autoPrompted, mode, points.length, placed.length]);

  const selected = placed.find((p) => p.id === selectedId);

  return (
    <Layout>
      <PageHeader
        eyebrow={`Floor Plan${planNumber ? ` · ${planNumber}` : ""}`}
        title={
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-transparent outline-none border-b-2 border-line focus:border-brand min-w-[280px]"
            data-testid="fp-name"
          />
        }
        subtitle={
          mode === "draw"
            ? "Draw mode · click to drop corners · click first corner to close"
            : "Manual layout mode · pick a rack from the library and drop it · drag to move · click to select"
        }
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/floorplan")} className="btn-ghost" data-testid="fp-list-btn">
              <ArrowLeft size={14} /> Plans
            </button>
            <button onClick={save} className="btn-primary" data-testid="fp-save-btn">
              <Save size={14} /> Save
            </button>
            {closed && points.length >= 3 && (
              <button onClick={() => setAutoOpen(true)} className="btn-secondary" data-testid="fp-auto-btn">
                <Sparkles size={14} /> Auto-Layout
              </button>
            )}
            {layoutId && closed && (
              <button onClick={() => navigate(`/floorplan/${layoutId}/cad`)} className="btn-ghost" data-testid="fp-print-cad-btn">
                <Printer size={14} /> Print CAD
              </button>
            )}
            {mode === "manual" && (
              <button onClick={generateQuote} className="btn-secondary" data-testid="fp-gen-quote-btn">
                <FileText size={14} /> Generate quotation
              </button>
            )}
          </div>
        }
      />

      {/* Mode tabs */}
      <div className="px-8 pt-2">
        <div className="inline-flex border border-line bg-white">
          <button
            onClick={() => setMode("draw")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold ${mode === "draw" ? "bg-ink text-white" : "text-ink-muted hover:bg-surfaceAlt"}`}
            data-testid="fp-mode-draw"
          >
            <Pencil size={12} className="inline mr-1.5" /> Draw shape
          </button>
          <button
            onClick={() => { if (points.length < 3) toast.error("Draw a shape first"); else setMode("manual"); }}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold ${mode === "manual" ? "bg-ink text-white" : "text-ink-muted hover:bg-surfaceAlt"}`}
            data-testid="fp-mode-manual"
          >
            <Move size={12} className="inline mr-1.5" /> Manual layout
          </button>
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 xl:grid-cols-5 gap-px bg-line border border-line">
        {/* LEFT controls */}
        <section className="xl:col-span-1 bg-white p-5 space-y-5" data-testid="fp-controls">
          {mode === "draw" ? (
            <>
              <div>
                <div className="label-tech mb-2">Unit</div>
                <select value={unit} onChange={(e) => setUnit(e.target.value)} className="input-tech mono text-xs" data-testid="fp-unit">
                  {UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                </select>
              </div>
              <div>
                <div className="label-tech mb-2">Grid size ({unit} / cell)</div>
                <input type="number" step="0.1" min="0.1" value={gridSize} onChange={(e) => setGridSize(Number(e.target.value) || 1)} className="input-tech mono text-xs" data-testid="fp-grid-size" />
              </div>

              <div className="flex flex-wrap gap-2">
                <button onClick={() => setSnap((s) => !s)} className={`btn-ghost text-xs ${snap ? "bg-orange-50 text-brand border-brand" : ""}`} data-testid="fp-toggle-snap"><Magnet size={12} /> Snap</button>
                <button onClick={() => setShowGrid((s) => !s)} className={`btn-ghost text-xs ${showGrid ? "bg-orange-50 text-brand border-brand" : ""}`} data-testid="fp-toggle-grid"><Grid3x3 size={12} /> Grid</button>
                <button onClick={undo} className="btn-ghost text-xs" disabled={!history.length} data-testid="fp-undo"><Undo2 size={12} /> Undo</button>
                <button onClick={redo} className="btn-ghost text-xs" disabled={!future.length} data-testid="fp-redo"><Redo2 size={12} /> Redo</button>
                <button onClick={reset} className="btn-danger text-xs" data-testid="fp-reset"><Trash2 size={12} /> Reset</button>
              </div>

              {!closed && points.length >= 3 && <div className="text-[0.7rem] text-brand mono">Click corner A again to close.</div>}

              <div className="pt-4 border-t border-line">
                <div className="label-tech mb-2 flex items-center gap-2"><MapPin size={12} /> Markers</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {MARKER_KINDS.map((m) => (
                    <button key={m.v} onClick={() => setPendingMarkerKind(m.v)} className={`text-[0.65rem] px-2 py-1.5 border ${pendingMarkerKind === m.v ? "border-brand bg-orange-50 text-brand" : "border-line"} flex items-center gap-1.5`} data-testid={`fp-marker-${m.v}`}>
                      <span style={{ width: 8, height: 8, background: m.c, borderRadius: 2, display: "inline-block" }} />
                      <span className="uppercase tracking-wider font-bold">{m.l}</span>
                    </button>
                  ))}
                </div>
                {pendingMarkerKind && <div className="text-[0.7rem] text-brand mt-2">Click on canvas to place • <button onClick={() => setPendingMarkerKind(null)} className="underline">cancel</button></div>}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setSnap((s) => !s)} className={`btn-ghost text-xs ${snap ? "bg-orange-50 text-brand border-brand" : ""}`}><Magnet size={12} /> Snap</button>
                <button onClick={undo} className="btn-ghost text-xs" disabled={!history.length}><Undo2 size={12} /> Undo</button>
                <button onClick={redo} className="btn-ghost text-xs" disabled={!future.length}><Redo2 size={12} /> Redo</button>
              </div>
              <div className="pt-3 border-t border-line">
                <div className="label-tech mb-2 flex items-center gap-2"><Move size={12} className="text-brand" /> Rack library</div>
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                  {library.map((lib) => (
                    <button
                      key={lib.id}
                      onClick={() => { setPendingLib(lib); toast.info("Click on the canvas to place"); }}
                      className={`w-full text-left text-[0.7rem] px-2 py-2 border ${pendingLib?.id === lib.id ? "border-brand bg-orange-50" : "border-line bg-white"} flex items-center gap-2 hover:bg-surfaceAlt`}
                      data-testid={`fp-lib-${lib.id}`}
                    >
                      <span style={{ width: 12, height: 12, background: lib.color, display: "inline-block", border: "1px solid #0f172a" }} />
                      <span className="flex-1">
                        <div className="mono font-bold text-ink">{lib.name}</div>
                        <div className="text-ink-muted text-[0.65rem]">{lib.default_length_in ?? (lib.default_length_ft * 12)}×{lib.default_depth_in ?? (lib.default_depth_ft * 12)}″ · {lib.pricing_mode === "per_ft" ? `₹${lib.price_per_ft}/ft` : `₹${lib.unit_price}/unit`}</div>
                      </span>
                    </button>
                  ))}
                </div>
                {pendingLib && <div className="text-[0.7rem] text-brand mt-2">Click on canvas to drop "{pendingLib.name}" • <button onClick={() => setPendingLib(null)} className="underline">cancel</button></div>}
              </div>

              {warnings.length > 0 && (
                <div className="pt-3 border-t border-line" data-testid="fp-warnings">
                  <div className="label-tech mb-2 flex items-center gap-2"><AlertTriangle size={12} className="text-amber-600" /> Live warnings</div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {warnings.map((w, i) => (
                      <div key={i} className={`text-[0.7rem] px-2 py-1.5 border-l-2 ${w.level === "error" ? "border-red-600 bg-red-50 text-red-900" : w.level === "warn" ? "border-amber-500 bg-amber-50 text-amber-900" : "border-sky-500 bg-sky-50 text-sky-900"}`}>
                        <span className="uppercase tracking-wider font-bold mr-1.5">{w.kind.replace(/_/g, " ")}</span>
                        {w.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="pt-4 border-t border-line">
            <div className="label-tech mb-2">Stats</div>
            <div className="grid grid-cols-2 gap-y-2 text-xs">
              <span className="text-ink-muted">Corners</span>
              <span className="mono font-bold text-right" data-testid="fp-stat-corners">{points.length}</span>
              <span className="text-ink-muted">Status</span>
              <span className="mono font-bold text-right">{closed ? "Closed" : "Open"}</span>
              <span className="text-ink-muted">Perimeter</span>
              <span className="mono font-bold text-right" data-testid="fp-stat-perimeter">{fmt(stats.perimeter)} {unit}</span>
              <span className="text-ink-muted">Area</span>
              <span className="mono font-bold text-right text-brand" data-testid="fp-stat-area">{fmt(stats.area)} {unit}²</span>
              <span className="text-ink-muted">Placed racks</span>
              <span className="mono font-bold text-right" data-testid="fp-stat-placed">{placed.length}</span>
            </div>
          </div>
        </section>

        {/* CANVAS */}
        <section className="xl:col-span-3 bg-surfaceAlt p-2" data-testid="fp-canvas-wrap">
          <svg
            ref={svgRef}
            viewBox={`-${PAD} -${PAD} ${VIEW + PAD * 2} ${VIEW + PAD * 2}`}
            onClick={mode === "draw" ? onCanvasClickDraw : onCanvasClickManual}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => { setDrag(null); setMarkerDrag(null); setItemDrag(null); setHoverPos(null); }}
            style={{ display: "block", width: "100%", height: "min(72vh, 720px)", background: "#fff", border: "1px solid #e5e7eb", cursor: closed && mode === "manual" ? (pendingLib || pendingMarkerKind ? "crosshair" : "default") : "crosshair" }}
            data-testid="fp-canvas"
          >
            {showGrid && (
              <g stroke="#f1f5f9" strokeWidth="1">
                {Array.from({ length: VIEW / CELL_PX + 1 }).map((_, i) => (
                  <line key={`v${i}`} x1={i * CELL_PX} y1={0} x2={i * CELL_PX} y2={VIEW} />
                ))}
                {Array.from({ length: VIEW / CELL_PX + 1 }).map((_, i) => (
                  <line key={`h${i}`} x1={0} y1={i * CELL_PX} x2={VIEW} y2={i * CELL_PX} />
                ))}
              </g>
            )}

            {closed && points.length >= 3 && (
              <polygon points={points.map((p) => `${p.x},${p.y}`).join(" ")} fill="#FFEDD5" fillOpacity={0.5} stroke="#FF6B1A" strokeWidth="3" />
            )}
            {!closed && points.length > 0 && (
              <polyline points={points.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#FF6B1A" strokeWidth="3" strokeLinejoin="round" />
            )}
            {mode === "draw" && !closed && points.length > 0 && hoverPos && (
              <line x1={points[points.length - 1].x} y1={points[points.length - 1].y} x2={hoverPos.x} y2={hoverPos.y} stroke="#FF6B1A" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
            )}

            {/* Side labels */}
            {sides.map((sd) => {
              const angle = Math.atan2(sd.dy, sd.dx);
              const nx = -Math.sin(angle), ny = Math.cos(angle);
              const off = 22;
              return (
                <text key={`sl${sd.index}`} x={sd.midX + nx * off} y={sd.midY + ny * off}
                  textAnchor="middle" dominantBaseline="middle"
                  fontFamily="monospace" fontSize="14" fontWeight="700"
                  fill={sd.override ? "#FF6B1A" : "#0f172a"}
                  style={{ pointerEvents: "none" }}>
                  {sd.label} · {fmt(sd.len)} {unit}
                </text>
              );
            })}

            {/* Corners */}
            {points.map((p, i) => (
              <g key={`pt${i}`}>
                <circle cx={p.x} cy={p.y} r={mode === "draw" ? 9 : 5}
                  fill={i === 0 && !closed && points.length >= 3 ? "#16a34a" : "#0f172a"}
                  stroke="#fff" strokeWidth="2"
                  style={{ cursor: mode === "draw" ? "move" : "default" }}
                  onMouseDown={(e) => onPointDown(i, e)}
                  onDoubleClick={(e) => { if (mode === "draw") { e.stopPropagation(); deletePoint(i); } }}
                  data-testid={`fp-pt-${i}`}
                />
                {mode === "draw" && (
                  <text x={p.x + 12} y={p.y - 12} fontFamily="monospace" fontWeight="700" fontSize="14" fill="#0f172a" style={{ pointerEvents: "none" }}>{ptLabel(i)}</text>
                )}
              </g>
            ))}

            {/* Placed items / rack rows with bay dividers + labels */}
            {placedItemsForCanvas.map((it) => {
              const cx = it.x + it.lengthPx / 2, cy = it.y + it.depthPx / 2;
              const lib = library.find((l) => l.id === it.library_item_id);
              const fill = lib?.color || "#e91e63";
              const isSel = selectedId === it.id;
              const hasError = warnings.some((w) => w.ids?.includes(it.id) && w.level === "error");
              const bays = it.bays || [];
              // Compute bay x-offsets (local to the row rect: left→right along length_in)
              // physical_row_length_in = sum(bays) + 1 + (n-1)*0.5 — already equals length_in.
              const pxPerInch = bays.length ? it.lengthPx / (it.length_in || 1) : 0;
              let cursor_in = 1.0;  // main leg allowance at row start
              const bayPieces = bays.map((bw, k) => {
                const x0_px = cursor_in * pxPerInch;
                const w_px  = bw * pxPerInch;
                cursor_in += bw + 0.5; // addon shared-leg gap
                return { x0: x0_px, w: w_px, bw, ut: (it.unit_types || [])[k] || (k === 0 ? "main" : "addon") };
              });
              return (
                <g key={it.id} transform={`rotate(${it.rotation} ${cx} ${cy})`}>
                  {/* Base row rectangle (magenta by default for rack rows) */}
                  <rect x={it.x} y={it.y} width={it.lengthPx} height={it.depthPx}
                    fill={fill} fillOpacity={0.88}
                    stroke={hasError ? "#dc2626" : isSel ? "#FF6B1A" : "#0f172a"}
                    strokeWidth={isSel || hasError ? 3 : 1.5}
                    style={{ cursor: it.locked ? "not-allowed" : "move" }}
                    onMouseDown={(e) => onItemDown(it, e)}
                    onClick={(e) => { e.stopPropagation(); setSelectedId(it.id); }}
                    data-testid={`fp-item-${it.id}`}
                  />
                  {/* Bay dividers */}
                  {bayPieces.length > 1 && bayPieces.slice(0, -1).map((b, k) => (
                    <line key={`div-${k}`}
                      x1={it.x + b.x0 + b.w} y1={it.y}
                      x2={it.x + b.x0 + b.w} y2={it.y + it.depthPx}
                      stroke="#ffffff" strokeOpacity={0.7} strokeWidth="1" />
                  ))}
                  {/* Bay size labels inside the row (only if bay width > 20px) */}
                  {bayPieces.map((b, k) => (
                    b.w >= 20 && (
                      <text key={`bl-${k}`}
                        x={it.x + b.x0 + b.w / 2}
                        y={it.y + it.depthPx / 2 + 4}
                        textAnchor="middle" fill="#fff"
                        fontFamily="monospace" fontSize={Math.min(12, Math.max(9, b.w / 4))}
                        fontWeight={700} style={{ pointerEvents: "none" }}>
                        {b.bw % 1 === 0 ? b.bw : b.bw.toFixed(1)}″
                      </text>
                    )
                  ))}
                  {/* Row name above, if space */}
                  {it.depthPx > 22 && (
                    <text x={cx} y={it.y + 11} textAnchor="middle" fill="#fff"
                      fontFamily="monospace" fontSize={9} fontWeight={700}
                      style={{ pointerEvents: "none", textShadow: "0 0 2px rgba(0,0,0,0.6)" }}>
                      {it.name.replace(/Wall Rack|Centre Gondola|Corner Rack/i, "").replace(/Main|Add-on/i, "").trim() || it.name}
                    </text>
                  )}
                  {it.locked && (
                    <text x={it.x + 6} y={it.y + 14} fill="#fff" fontSize={10} style={{ pointerEvents: "none" }}>🔒</text>
                  )}
                </g>
              );
            })}

            {/* Markers */}
            {markers.map((m, i) => {
              const def = MARKER_KINDS.find((k) => k.v === m.kind);
              return (
                <g key={m.id}>
                  <rect x={m.x - 14} y={m.y - 14} width={28} height={28}
                    fill={def?.c || "#000"} stroke="#fff" strokeWidth="2"
                    style={{ cursor: "move" }}
                    onMouseDown={(e) => { e.stopPropagation(); setMarkerDrag(i); }}
                    onDoubleClick={(e) => { e.stopPropagation(); removeMarker(m.id); }}
                  />
                  <text x={m.x} y={m.y + 4} textAnchor="middle" fill="#fff" fontFamily="monospace" fontSize="11" fontWeight="700" style={{ pointerEvents: "none" }}>
                    {def?.l[0] || "?"}
                  </text>
                </g>
              );
            })}
          </svg>
          <div className="text-[0.7rem] text-ink-muted mt-2 px-1 mono">
            {mode === "draw"
              ? "click = add corner · double-click corner = delete · drag corner = move · click first corner = close"
              : "click rack in library → click canvas to drop · drag rack = move · click rack = select · double-click marker = delete"}
          </div>
        </section>

        {/* RIGHT panel */}
        <section className="xl:col-span-1 bg-white p-5" data-testid="fp-right">
          {mode === "draw" ? (
            <>
              <div className="label-tech mb-3">Wall lengths</div>
              <div className="space-y-2">
                {sides.map((sd) => (
                  <div key={sd.index} className="grid grid-cols-[auto_1fr] gap-2 items-center">
                    <span className="mono text-xs font-bold w-12">{sd.label}</span>
                    <input type="number" step="0.01"
                      value={sd.override ? overrides[sd.index] : Number(sd.computedLen.toFixed(2))}
                      onChange={(e) => setSideLen(sd.index, e.target.value)}
                      className={`input-tech mono text-xs ${sd.override ? "border-brand text-brand" : ""}`}
                      data-testid={`fp-side-${sd.index}`}
                    />
                  </div>
                ))}
              </div>
              {sides.some((s) => s.override) && (
                <div className="text-[0.7rem] text-brand mono mt-3 flex items-center gap-1.5">
                  <CheckCircle2 size={12} /> orange = manual override
                </div>
              )}
              {markers.length > 0 && (
                <div className="mt-6 pt-4 border-t border-line">
                  <div className="label-tech mb-3">Placed markers</div>
                  <div className="space-y-1.5">
                    {markers.map((m) => {
                      const def = MARKER_KINDS.find((k) => k.v === m.kind);
                      return (
                        <div key={m.id} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            <span style={{ width: 8, height: 8, background: def?.c, borderRadius: 2, display: "inline-block" }} />
                            {def?.l}
                          </span>
                          <button onClick={() => removeMarker(m.id)} className="btn-danger text-[0.65rem] py-0.5"><Trash2 size={10} /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : selected ? (
            <SelectedItemPanel
              item={selected}
              library={library}
              onResize={(l, d) => resizeItem(selected.id, l, d)}
              onRotate={(deg) => rotateItem(selected.id, deg)}
              onDuplicate={() => duplicateItem(selected.id)}
              onDelete={() => removeItem(selected.id)}
              onToggleLock={() => toggleLock(selected.id)}
            />
          ) : (
            <div className="text-xs text-ink-muted">
              <div className="label-tech mb-2">Selection</div>
              Pick an item on the canvas to edit its size, rotate, lock, duplicate or delete.
              <div className="mt-6 pt-4 border-t border-line">
                <div className="label-tech mb-2">Quick summary</div>
                <div className="text-xs space-y-1.5">
                  {placed.length === 0 ? (
                    <div className="text-ink-muted">No racks placed yet.</div>
                  ) : (
                    Object.entries(placed.reduce((acc, p) => { acc[p.name] = (acc[p.name] || 0) + 1; return acc; }, {})).map(([n, c]) => (
                      <div key={n} className="flex justify-between"><span>{n}</span><span className="mono font-bold">×{c}</span></div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {autoOpen && (
        <AutoLayoutModal
          config={autoConfig}
          setConfig={setAutoConfig}
          busy={autoBusy}
          hasExisting={placed.length > 0}
          onRun={runAutoLayout}
          onClose={() => setAutoOpen(false)}
        />
      )}
    </Layout>
  );
}

function AutoLayoutModal({ config, setConfig, busy, hasExisting, onRun, onClose }) {
  const setF = (k) => (e) => setConfig({ ...config, [k]: Number(e.target.value) });
  const setB = (k) => (e) => setConfig({ ...config, [k]: e.target.checked });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="fp-auto-modal"
    >
      <div
        className="bg-white border border-line w-full max-w-2xl shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-surfaceAlt sticky top-0">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-brand" />
            <div className="mono font-bold uppercase tracking-wider text-sm">Adarsh Udyog · Auto-Layout Engine</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="fp-auto-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4 text-xs">
          <div className="text-ink-muted leading-relaxed">
            Racks are auto-placed using Adarsh Udyog thumb rules: <b>Main +1″ leg · Add-on 0.5″ shared-leg gap</b>.
            Preferred bay <b>36″</b>, falls back to 48/30/24/18/15/12″ for row ends.
            All values below are in <b>inches</b>.
          </div>

          <div className="pt-2 border-t border-line">
            <div className="label-tech mb-2 text-[0.65rem]">Wall Racks</div>
            <div className="grid grid-cols-3 gap-3">
              <Labelled label="Preferred bay (in)">
                <input type="number" step="1" min="12" max="48" value={config.preferred_bay_in}
                       onChange={setF("preferred_bay_in")} className="input-tech mono text-xs"
                       data-testid="fp-auto-bay" />
              </Labelled>
              <Labelled label="Wall depth (in)">
                <input type="number" step="1" min="12" max="36" value={config.wall_depth_in}
                       onChange={setF("wall_depth_in")} className="input-tech mono text-xs"
                       data-testid="fp-auto-walldepth" />
              </Labelled>
              <Labelled label="Entrance clearance (in)">
                <input type="number" step="6" min="0" max="120" value={config.entrance_clear_in}
                       onChange={setF("entrance_clear_in")} className="input-tech mono text-xs"
                       data-testid="fp-auto-clear" />
              </Labelled>
            </div>
          </div>

          <div className="pt-3 border-t border-line">
            <div className="label-tech mb-2 text-[0.65rem]">Aisles / Walkway</div>
            <div className="grid grid-cols-2 gap-3">
              <Labelled label="Aisle preferred (in)">
                <input type="number" step="6" min="36" max="96" value={config.aisle_pref_in}
                       onChange={setF("aisle_pref_in")} className="input-tech mono text-xs"
                       data-testid="fp-auto-aisle" />
              </Labelled>
              <Labelled label="Aisle minimum (in)">
                <input type="number" step="6" min="30" max="60" value={config.aisle_min_in}
                       onChange={setF("aisle_min_in")} className="input-tech mono text-xs"
                       data-testid="fp-auto-aisle-min" />
              </Labelled>
            </div>
          </div>

          <div className="pt-3 border-t border-line space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={config.include_corners} onChange={setB("include_corners")}
                     data-testid="fp-auto-corners" />
              <span>Place <b>Corner Rack</b> at 90° vertices (3 legs, L-shape filler)</span>
            </label>
            {config.include_corners && (
              <div className="grid grid-cols-1 gap-3 pl-6">
                <Labelled label="Corner size (in)">
                  <input type="number" step="6" min="18" max="48" value={config.corner_size_in}
                         onChange={setF("corner_size_in")} className="input-tech mono text-xs" />
                </Labelled>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-line space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={config.include_centre} onChange={setB("include_centre")}
                     data-testid="fp-auto-centre" />
              <span>Add <b>Centre Gondola</b> rows (2-sided, {config.centre_depth_in}″ total depth)</span>
            </label>
            {config.include_centre && (
              <div className="grid grid-cols-3 gap-3 pl-6">
                <Labelled label="Gondola bay (in)">
                  <input type="number" step="6" min="24" max="48" value={config.centre_bay_in}
                         onChange={setF("centre_bay_in")} className="input-tech mono text-xs" />
                </Labelled>
                <Labelled label="Gondola depth (in)">
                  <input type="number" step="6" min="24" max="60" value={config.centre_depth_in}
                         onChange={setF("centre_depth_in")} className="input-tech mono text-xs" />
                </Labelled>
                <Labelled label="Max centre rows">
                  <input type="number" step="1" min="1" max="8" value={config.max_centre_rows}
                         onChange={setF("max_centre_rows")} className="input-tech mono text-xs" />
                </Labelled>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-line space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={config.auto_billing ?? true} onChange={setB("auto_billing")}
                     data-testid="fp-auto-billing" />
              <span>Auto-place <b>Billing Counter</b> near Entrance marker (60″ × 24″)</span>
            </label>
          </div>

          {hasExisting && (
            <label className="flex items-center gap-2 border-t border-line pt-3">
              <input type="checkbox" checked={config.replace_existing}
                     onChange={setB("replace_existing")} data-testid="fp-auto-replace" />
              <span>Replace existing placed racks (uncheck to append)</span>
            </label>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line bg-surfaceAlt sticky bottom-0">
          <button onClick={onClose} className="btn-ghost" data-testid="fp-auto-cancel">Cancel</button>
          <button onClick={onRun} disabled={busy} className="btn-primary" data-testid="fp-auto-run">
            <Sparkles size={14} /> {busy ? "Generating…" : "Generate layout"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labelled({ label, children }) {
  return (
    <div>
      <label className="label-tech text-[0.6rem] mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function SelectedItemPanel({ item, library, onResize, onRotate, onDuplicate, onDelete, onToggleLock }) {
  const lib = library.find((l) => l.id === item.library_item_id);
  const isRow = Array.isArray(item.bays) && item.bays.length > 1;
  const lenIn = item.length_in ?? (item.length_ft * 12);
  const depIn = item.depth_in  ?? (item.depth_ft * 12);
  return (
    <div data-testid="fp-selected">
      <div className="label-tech mb-3">Selected · {item.name}</div>
      <div className="space-y-3">
        {isRow ? (
          <div>
            <label className="label-tech text-[0.65rem] mb-1 block">Bay sequence (″)</label>
            <div className="bg-surfaceAlt border border-line p-2 mono text-xs" data-testid="fp-sel-bays">
              {item.bays.map((b, i) => (
                <span key={i} className={`inline-block px-1.5 py-0.5 mr-1 mb-1 border ${(item.unit_types || [])[i] === "main" ? "bg-brand text-white border-brand" : "bg-white text-ink border-line"}`}>
                  {b % 1 === 0 ? b : b.toFixed(1)}
                </span>
              ))}
            </div>
            <div className="text-[0.65rem] text-ink-muted mt-1">
              {item.bays.length} bays · total row {lenIn.toFixed(1)}″ × {depIn}″ deep
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Length (in)</label>
              <input type="number" step="1" value={Math.round(lenIn)}
                onChange={(e) => onResize(Number(e.target.value) / 12, depIn / 12)}
                className="input-tech mono text-xs" data-testid="fp-sel-length" />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Depth (in)</label>
              <input type="number" step="1" value={Math.round(depIn)}
                onChange={(e) => onResize(lenIn / 12, Number(e.target.value) / 12)}
                className="input-tech mono text-xs" data-testid="fp-sel-depth" />
            </div>
          </>
        )}
        <div>
          <label className="label-tech text-[0.65rem] mb-1 block">Rotation</label>
          <div className="mono text-xs">{Math.round(item.rotation)}°</div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button onClick={() => onRotate(90)} className="btn-ghost text-xs" data-testid="fp-sel-rotate"><RotateCw size={12} /> Rotate 90°</button>
          <button onClick={onDuplicate} className="btn-ghost text-xs" data-testid="fp-sel-duplicate"><Copy size={12} /> Duplicate</button>
          <button onClick={onToggleLock} className={`btn-ghost text-xs ${item.locked ? "bg-orange-50 text-brand border-brand" : ""}`} data-testid="fp-sel-lock">
            {item.locked ? <Unlock size={12} /> : <Lock size={12} />} {item.locked ? "Unlock" : "Lock"}
          </button>
          <button onClick={onDelete} className="btn-danger text-xs" data-testid="fp-sel-delete"><Trash2 size={12} /> Delete</button>
        </div>
        <div className="pt-3 border-t border-line text-[0.7rem] text-ink-muted space-y-0.5">
          <div>Kind: <span className="mono font-bold">{lib?.kind}</span> · Unit: <span className="mono font-bold uppercase">{lib?.unit_type || "main"}</span></div>
          <div>Legs/unit: <span className="mono">{lib?.leg_count || 2}</span> · Weight: {lib?.weight_kg || 0} kg/unit</div>
          <div>Unit price: {fmtINR(lib?.unit_price || 0)}</div>
        </div>
      </div>
    </div>
  );
}
